import { useEffect, useState, useCallback, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Inbox, KanbanSquare, CalendarClock, CheckSquare,
  BarChart3, Settings, Bell, Search, LogOut, ChevronDown,
  PhoneCall, CalendarDays, Briefcase, LifeBuoy, Sun, Moon, Laptop,
  MessageSquareWarning, ShieldCheck, IndianRupee, Menu, AlertTriangle,
  BellRing, BellOff, GraduationCap, BookOpen, Video,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  getBrowserNotificationState, requestBrowserNotificationPermission,
  disableBrowserNotifications, showBrowserNotification,
} from "@/lib/browserNotifications";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { TestBadge } from "@/components/common";
import { TaskReviewHost } from "@/components/TaskReviewPrompt";
import { ProviderStateNote } from "@/components/ProviderStateNote";
import { showTaskReview } from "@/lib/taskReview";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, testid: "nav-home" },
  { to: "/pipelines", label: "Pipelines", icon: KanbanSquare, testid: "nav-pipelines" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/calls", label: "Calls", icon: PhoneCall, testid: "nav-calls" },
  { to: "/consultations", label: "Consultations", icon: CalendarClock, testid: "nav-consultations" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/process", label: "Process / Cases", icon: Briefcase, testid: "nav-process" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
  { to: "/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  // 2026-09 — same gate as every webinar read endpoint (webinar.view or
  // webinar.config); roles without either never see the link.
  { to: "/webinars", label: "Webinars", icon: Video, testid: "nav-webinars", anyCap: ["webinar.view", "webinar.config"] },
  // Reachable by Leadership (approvals.manage) and by Supervisors via the
  // Unassigned Tasks queue (tasks.assign) — see routers_extras.approval_workqueue.
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, testid: "nav-approvals", anyCap: ["approvals.manage", "tasks.assign"] },
  { to: "/finance", label: "Finance", icon: IndianRupee, testid: "nav-finance", cap: "finance.view" },
  // 2026-09 P5B.1 correction — Reports is company/team-wide leadership
  // visibility (Admin/Manager/Supervisor, or another role explicitly
  // granted reports.view), never an ordinary employee's own personal-
  // performance portal. The backend already enforces this on every
  // reports_router endpoint (require_capability("reports.view")); this
  // mirrors it on the nav link itself so an unauthorized employee never
  // even sees a link that would 403.
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", cap: "reports.view" },
  { to: "/my-coaching", label: "My Coaching", icon: GraduationCap, testid: "nav-my-coaching", cap: "ai.coaching.view_own" },
  { to: "/knowledge-base", label: "Knowledge Base", icon: BookOpen, testid: "nav-knowledge-base" },
];

/* ---- Theme (System | Light | Dark), persisted per user ---- */
export const applyTheme = (mode) => {
  const dark = mode === "dark" ||
    (mode !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
  localStorage.setItem("salesos-theme", mode);
};

const ThemeToggle = () => {
  const [mode, setMode] = useState(localStorage.getItem("salesos-theme") || "system");
  useEffect(() => { applyTheme(mode); }, [mode]);
  const set = (m) => {
    setMode(m);
    api.patch("/users/me/prefs", { theme: m }).catch(() => {});
  };
  const Icon = mode === "dark" ? Moon : mode === "light" ? Sun : Laptop;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid="theme-toggle" variant="ghost" size="icon"><Icon className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="theme-system" onClick={() => set("system")}><Laptop className="h-4 w-4 mr-2" /> System</DropdownMenuItem>
        <DropdownMenuItem data-testid="theme-light" onClick={() => set("light")}><Sun className="h-4 w-4 mr-2" /> Light</DropdownMenuItem>
        <DropdownMenuItem data-testid="theme-dark" onClick={() => set("dark")}><Moon className="h-4 w-4 mr-2" /> Dark</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/* ---- Internal UAT feedback widget (global) ---- */
const UatWidget = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [priority, setPriority] = useState("minor");
  const [desc, setDesc] = useState("");
  const location = useLocation();
  const submit = async () => {
    try {
      await api.post("/uat/feedback", {
        type, priority, description: desc,
        page_url: location.pathname + location.search,
      });
      toast.success("Thank you — feedback logged for the build team");
      setOpen(false); setDesc("");
    } catch { toast.error("Could not submit feedback"); }
  };
  return (
    <>
      {/* R15 U2: this floating button sits over the bottom-right corner of every page, which is exactly where table pagers (Previous / Next) live - normal clicks on them opened
          this dialog instead. Three measures: <main> reserves space below the content (pb-20) so the last controls can always be scrolled clear of it; pagers carry scroll-mb-20 so
          keyboard focus scrolls them clear of it too; and below the sm breakpoint it is an icon only, so it covers a 32px square instead of a 130px pill. */}
      <Button data-testid="uat-feedback-button" variant="outline" size="sm" aria-label="Report problem"
        className="fixed bottom-4 right-4 z-40 shadow-md h-8 text-xs bg-card px-2 sm:px-3"
        onClick={() => setOpen(true)}>
        <MessageSquareWarning className="h-3.5 w-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Report problem</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Report Problem / Suggest Improvement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="uat-type-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="missing_feature">Missing Feature</SelectItem>
                    <SelectItem value="difficult">Difficult to Use</SelectItem>
                    <SelectItem value="incorrect_data">Incorrect Data</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="uat-priority-select" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blocking">Blocking</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">What happened / what do you need?</Label>
              <Textarea data-testid="uat-description" rows={4} value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Describe the issue, where it happened and what you expected…" />
            </div>
            <p className="text-[11px] text-muted-foreground">Your name, this page URL and the app version are recorded automatically.</p>
          </div>
          <DialogFooter>
            <Button data-testid="uat-submit" disabled={desc.trim().length < 5} onClick={submit}>Submit feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const GlobalSearch = ({ open, setOpen }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  // R15 U4: search hides TEST customers (correct for ordinary business-ID search). An administrator who needs to find a TEST customer gets an EXPLICIT, per-search switch - never on by
  // default, never remembered, shown only to someone the server would honour it for, and every TEST result is badged.
  const { hasCap } = useAuth();
  const canIncludeTest = !!hasCap("admin.config");
  const [includeTest, setIncludeTest] = useState(false);
  useEffect(() => { if (!open) setIncludeTest(false); }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}${canIncludeTest && includeTest ? "&include_test=true" : ""}`);
        setResults(data);
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, includeTest, canIncludeTest]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Global customer search"
                  description="Search customers by name, phone, email, Customer ID (AP-…) or Case ID (CS-…).">
      <CommandInput data-testid="global-search-input" placeholder="Search name, phone, email or Customer ID (AP-…)" value={q} onValueChange={setQ} />
      <CommandList>
        {canIncludeTest && (
          <label className="flex items-center gap-2 px-3 py-1.5 text-xs border-b" data-testid="global-search-include-test">
            <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="global-search-include-test-box" />
            <span>Include TEST customers</span>
            <span className="text-muted-foreground">{includeTest ? "— TEST results are badged" : "— TEST customers are hidden from this search"}</span>
          </label>
        )}
        <CommandEmpty>{q.length < 2 ? "Type at least 2 characters…" : (canIncludeTest && !includeTest ? "No customers found. TEST customers are hidden — tick “Include TEST customers” to search them." : "No customers found.")}</CommandEmpty>
        {results.length > 0 && (
          <CommandGroup heading="Customers">
            {results.map((r) => (
              <CommandItem
                key={r.contact_id}
                data-testid={`search-result-${r.customer_id}`}
                value={`${r.full_name} ${r.customer_id} ${r.primary_phone || ""}`}
                onSelect={() => { setOpen(false); setQ(""); navigate(`/customers/${r.contact_id}`); }}
                className="flex items-center gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{r.full_name}</span>
                    <TestBadge show={r.is_test_record} />
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{r.customer_id} · {r.primary_phone || r.email || ""}</div>
                </div>
                <span className="text-[10px] text-muted-foreground uppercase">{r.match}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

// Every notify() call attaches a `related` payload meant for click-through,
// but different call sites carry different fields depending on what kind of
// record the notification is about — resolve whichever is present into an
// actual route, preferring an explicit link when the backend already built one.
const notificationHref = (related) => {
  const r = related || {};
  if (r.link) return r.link;
  if (r.contact_id) return `/customers/${r.contact_id}`;
  if (r.approval_id) return "/approvals";
  if (r.ticket_id) return "/support";
  if (r.task_id) return "/tasks";
  if (r.appointment_id) return "/consultations";
  if (r.opportunity_id) return "/pipelines";
  if (r.digest_id) return "/reports";
  return null;
};

// 2026-09 Section 7 fix — this label/hint pair is ONLY about the browser's
// own OS-level desktop-popup permission, never about SalesOS itself. The
// wording used to just say "Blocked by browser" (+ the static "while this
// tab is open" suffix below), which read as ambiguous about whose failure
// it was; a production reviewer flagged it as easy to misread as "SalesOS
// failed". Now explicit ("Your browser blocked…") plus a hover hint that
// says outright that in-app notifications (the bell/list right below) are
// unaffected — they come from GET /notifications regardless of this state,
// never gated on Notification.permission (see NotificationsBell.load below).
const BROWSER_NOTIF_LABEL = {
  enabled: "Browser notifications on", not_enabled: "Enable browser notifications",
  blocked: "Your browser blocked desktop notifications", unsupported: "Not supported in this browser",
};
const BROWSER_NOTIF_HINT = {
  blocked: "This is your browser's own setting, not a SalesOS failure — desktop pop-ups only. "
    + "SalesOS's own in-app notifications below are unaffected and stay up to date.",
  unsupported: "This browser doesn't support desktop pop-ups — not a SalesOS failure. "
    + "SalesOS's own in-app notifications below are unaffected.",
  not_enabled: "Desktop pop-ups are off. SalesOS's own in-app notifications below always work regardless.",
  enabled: "Desktop pop-ups are on for this tab, in addition to SalesOS's own in-app notifications below.",
};

// Exported (2026-09 Section 7) so AppShell.notificationsBell.test.jsx can
// mount it directly — the bell/in-app list must be provably independent of
// desktop Notification.permission without dragging in the rest of AppShell's
// nav chrome.
export const NotificationsBell = () => {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [browserState, setBrowserState] = useState(() => getBrowserNotificationState());
  const navigate = useNavigate();
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      // Fire a real OS/browser notification for anything newly-unread
      // since the last poll (never on the very first load — that would
      // re-notify for the whole existing backlog the instant someone
      // enables this).
      if (!firstLoad.current) {
        for (const n of data.items) {
          if (!n.read && !seenIds.current.has(n.notification_id)) {
            showBrowserNotification(n, (clicked) => {
              const href = notificationHref(clicked.related);
              if (href) navigate(href);
            });
          }
        }
      }
      firstLoad.current = false;
      seenIds.current = new Set(data.items.map((n) => n.notification_id));
      setData(data);
    } catch { /* transient — next poll retries */ }
  }, [navigate]);

  useEffect(() => { load(); const i = setInterval(load, 45000); return () => clearInterval(i); }, [load]);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  const openNotification = async (n) => {
    const href = notificationHref(n.related);
    if (!n.read) { try { await api.patch(`/notifications/${n.notification_id}/read`); } catch {} }
    // "You still have N open task(s)" - opens the same non-blocking review panel (the
    // reminder stays here until every task has been dealt with).
    if (n.kind === "task_review" && n.related?.review_id) {
      setOpen(false); showTaskReview({ review_id: n.related.review_id }); load();
      return;
    }
    if (href) { setOpen(false); navigate(href); }
    load();
  };

  const toggleBrowserNotifications = async () => {
    if (browserState === "enabled") {
      disableBrowserNotifications();
      setBrowserState(getBrowserNotificationState());
      return;
    }
    if (browserState === "blocked" || browserState === "unsupported") return;
    const result = await requestBrowserNotificationPermission();
    setBrowserState(getBrowserNotificationState());
    if (result === "denied") toast.error("Browser notification permission was denied.");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button data-testid="notifications-bell" variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-[hsl(var(--danger))] text-[10px] text-white flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          <Button data-testid="notifications-mark-all-read" variant="ghost" size="sm" className="h-7 text-xs" onClick={markAll}>Mark all read</Button>
        </div>
        <button type="button" data-testid="browser-notifications-toggle"
          disabled={browserState === "blocked" || browserState === "unsupported"}
          onClick={toggleBrowserNotifications} title={BROWSER_NOTIF_HINT[browserState]}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground border-b hover:bg-[hsl(var(--accent))]/40 disabled:hover:bg-transparent disabled:opacity-70 disabled:cursor-not-allowed">
          {browserState === "enabled" ? <BellRing className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> : <BellOff className="h-3.5 w-3.5" />}
          {BROWSER_NOTIF_LABEL[browserState]}
          <span className="ml-auto text-[10px]">while this tab is open</span>
        </button>
        <ScrollArea className="h-80">
          {data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">No notifications yet.</p>
          ) : data.items.map((n) => {
            const clickable = !!notificationHref(n.related);
            const urgent = n.kind === "urgent";
            return (
              <div key={n.notification_id} data-testid={`notification-item-${n.notification_id}`}
                className={cn("px-3 py-2 border-b last:border-0 text-sm", !n.read && "bg-[hsl(var(--accent))]/50", urgent && "bg-[hsl(var(--danger))]/10", clickable && "cursor-pointer hover:bg-[hsl(var(--accent))]/70")}
                onClick={clickable ? () => openNotification(n) : undefined}>
                <div className="flex items-center gap-2">
                  {urgent && <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--danger))] shrink-0" />}
                  <span className={cn("font-medium flex-1", urgent && "text-[hsl(var(--danger))]")}>{n.title}</span>
                  <TestBadge show={n.is_test_record} />
                </div>
                <p className="text-xs text-muted-foreground">{n.body}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{fmtIST(n.created_at, { zone: true })}</p>
              </div>
            );
          })}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export const AppShell = ({ children }) => {
  const { user, logout, hasCap } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    applyTheme(user?.prefs?.theme || localStorage.getItem("salesos-theme") || "system");
  }, [user]);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen((o) => !o); }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const navLinks = (testidPrefix = "") => (
    <>
      {NAV.filter((item) => (!item.cap || hasCap(item.cap))
        && (!item.anyCap || item.anyCap.some((c) => hasCap(c)))).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={`${testidPrefix}${item.testid}`}
          end={item.to === "/"}
          className={({ isActive }) => cn(
            "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors",
            isActive ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
      {hasCap("admin.config") && (
        <NavLink to="/admin" data-testid={`${testidPrefix}nav-admin`} className={({ isActive }) => cn(
          "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors",
          isActive ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}>
          <Settings className="h-4 w-4" />
          Administration
        </NavLink>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]/40 flex">
      {/* Desktop sidebar */}
      <aside className="w-60 shrink-0 border-r bg-card hidden md:flex flex-col fixed inset-y-0" data-testid="app-sidebar">
        <div className="h-14 flex items-center px-4 border-b">
          <span className="font-heading font-semibold tracking-tight">Aussie Pathways</span>
          <Badge variant="outline" className="ml-2 text-[10px] font-mono">SalesOS</Badge>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {navLinks()}
        </nav>
        <ProviderStateNote />
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="h-14 border-b bg-card/80 backdrop-blur sticky top-0 z-40 flex items-center gap-2 sm:gap-3 px-3 sm:px-4">
          {/* Mobile hamburger drawer */}
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-nav-trigger" variant="ghost" size="icon" className="md:hidden shrink-0">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-nav-drawer">
              <SheetHeader className="h-14 flex flex-row items-center px-4 border-b space-y-0">
                <SheetTitle className="font-heading font-semibold tracking-tight text-base flex items-center">
                  Aussie Pathways
                  <Badge variant="outline" className="ml-2 text-[10px] font-mono">SalesOS</Badge>
                </SheetTitle>
              </SheetHeader>
              <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                {navLinks("mobile-")}
              </nav>
              <ProviderStateNote />
            </SheetContent>
          </Sheet>

          <Button
            data-testid="global-search-trigger"
            variant="outline"
            className="w-full max-w-md justify-start text-muted-foreground font-normal h-9 min-w-0"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="h-4 w-4 mr-2 shrink-0" />
            <span className="truncate">Search customers…</span>
            <kbd className="ml-auto text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded hidden sm:inline">Ctrl K</kbd>
          </Button>
          <div className="ml-auto flex items-center gap-0.5 sm:gap-1 shrink-0">
            <ThemeToggle />
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-trigger" variant="ghost" className="gap-2 pl-1.5 h-9">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-xs">{(user?.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm hidden sm:inline max-w-32 truncate">{user?.name}</span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                  <Badge variant="outline" className="mt-1 text-[10px] capitalize">{user?.role}</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout}>
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 px-3 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-20" data-testid="app-main">{children}</main>
      </div>
      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
      <TaskReviewHost />
      <UatWidget />
    </div>
  );
};
